import PropTypes from 'prop-types';
import classNames from 'classnames';
import s from './SingleRangeSlider.module.scss';
import React, { useCallback, useEffect, useRef } from 'react';

const THUMB_DIAMETER = 16;

const SingleRangeSlider = ({
    min,
    max,
    value,
    onChange = () => {},
    valueText = '',
    discreteValues = null,
    format = null,
    // The ends of the range above the track and the value riding the thumb. A
    // caller whose value is read somewhere better — a date field above the
    // slider — switches them off.
    showLabels = true,
}) => {
    // The labels' reading of a value; the default is the number itself with
    // the unit suffix, the way the slider has always printed it.
    const readAs = format || ((val) => val + valueText);
    const sliderRef = useRef(null);
    const rangeRef = useRef(null);
    const labelRef = useRef(null);

    const getPercent = useCallback(
        (val) => {
            if (discreteValues && discreteValues.length) {
                const idx = discreteValues.indexOf(val);
                if (idx === -1) return 0;
                return idx / (discreteValues.length - 1);
            }
            return (val - min) / (max - min);
        },
        [min, max, discreteValues]
    );

    useEffect(() => {
        const pct = getPercent(value);
        if (rangeRef.current && sliderRef.current) {
            rangeRef.current.style.width = `${pct * 100}%`;

            const sliderWidth = sliderRef.current.offsetWidth;
            const trackWidth = sliderWidth - THUMB_DIAMETER;
            const thumbCenterPx = pct * trackWidth + THUMB_DIAMETER / 2;

            if (labelRef.current) {
                labelRef.current.style.left = `${thumbCenterPx}px`;
            }
        }
    }, [value, getPercent]);

    const handleChange = useCallback(
        (e) => {
            const v = +e.target.value;
            const newValue = discreteValues
                ? discreteValues[
                      Math.round((v / 100) * (discreteValues.length - 1))
                  ]
                : v;
            onChange(newValue);
        },
        [discreteValues, onChange]
    );

    const sliderMin = discreteValues ? 0 : min;
    const sliderMax = discreteValues ? 100 : max;
    const sliderVal = discreteValues ? getPercent(value) * 100 : value;

    return (
        <div
            className={classNames(s.Root, {
                [s['Root--Bare']]: !showLabels,
            })}>
            {showLabels && (
                <div className={s.LabelsTop}>
                    <div className={s.LeftValue}>
                        {readAs(discreteValues ? discreteValues[0] : min)}
                    </div>
                    <div className={s.RightValue}>
                        {readAs(
                            discreteValues
                                ? discreteValues[discreteValues.length - 1]
                                : max
                        )}
                    </div>
                </div>
            )}

            <div className={s.SliderContainer}>
                <input
                    type="range"
                    ref={sliderRef}
                    min={sliderMin}
                    max={sliderMax}
                    step={
                        discreteValues ? 100 / (discreteValues.length - 1) : 1
                    }
                    value={sliderVal}
                    onChange={handleChange}
                    className={s.Thumb}
                />

                <div className={s.Slider}>
                    <div className={s.Track} />
                    <div ref={rangeRef} className={s.Range} />

                    {showLabels && (
                        <div ref={labelRef} className={s.SliderLabel}>
                            {readAs(value)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

SingleRangeSlider.propTypes = {
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    valueText: PropTypes.string,
    discreteValues: PropTypes.arrayOf(PropTypes.number),
    // How the labels read a value — dates off day counts, say. The values the
    // slider trades in stay numbers either way; given a format, valueText is
    // its to use or drop.
    format: PropTypes.func,
    showLabels: PropTypes.bool,
};

export default SingleRangeSlider;
